import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { errMsg } from "./apiError";
import { Trophy, Plus, Pencil, Trash2, X, Check, Loader2, ImageIcon, Film, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059] w-full";

function DistinctionForm({ initial, onSaved, onClose }) {
  const [form, setForm] = useState({
    titre: initial?.titre || "",
    personne: initial?.personne || "",
    media_url: initial?.media_url || "",
    media_type: initial?.media_type || "",
    programme: initial?.programme || "",
    date_evenement: initial?.date_evenement || "",
    description: initial?.description || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, media_url: data.url, media_type: (data.content_type || "").startsWith("video") ? "video" : "photo" }));
      toast.success("Média téléversé");
    } catch (err) {
      toast.error(errMsg(err, "Téléversement impossible"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.titre.trim() || !form.personne.trim()) {
      toast.error("Le titre de la reconnaissance et le nom de la personne sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, media_url: form.media_url || null, media_type: form.media_url ? form.media_type || "photo" : null, programme: form.programme || null, date_evenement: form.date_evenement || null, description: form.description || null };
      if (initial?.id) {
        await axios.put(`${API}/admin/distinctions/${initial.id}`, payload, { withCredentials: true });
        toast.success("Reconnaissance mise à jour");
      } else {
        await axios.post(`${API}/admin/distinctions`, payload, { withCredentials: true });
        toast.success("Reconnaissance ajoutée au Mur des Légendes");
      }
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="distinction-modal">
      <div className="bg-[#121214] border border-[#C5A059]/40 w-full max-w-xl max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">{initial?.id ? "Modifier la reconnaissance" : "Ajouter une reconnaissance"}</p>
          <button onClick={onClose} className="text-[#71717A] hover:text-white transition-colors" aria-label="Fermer"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Titre de la reconnaissance / trophée *</label>
            <input value={form.titre} onChange={set("titre")} data-testid="distinction-titre" placeholder="Ex : Meilleur joueur — Détection Novembre 2026" className={`${inputCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Nom de la personne *</label>
            <input value={form.personne} onChange={set("personne")} data-testid="distinction-personne" placeholder="Ex : Jean Mukendi" className={`${inputCls} mt-1`} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Programme / détection liée</label>
              <input value={form.programme} onChange={set("programme")} data-testid="distinction-programme" placeholder="Ex : Kinshasa — Nov 2026" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Date de la remise</label>
              <input type="date" value={form.date_evenement} onChange={set("date_evenement")} data-testid="distinction-date" className={`${inputCls} mt-1 [color-scheme:dark]`} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Photo ou vidéo</label>
            <div className="flex items-center gap-3 mt-1">
              <label className="inline-flex items-center gap-2 border border-[#C5A059]/60 text-[#C5A059] px-4 py-2 text-xs uppercase tracking-[0.15em] cursor-pointer hover:bg-[#C5A059] hover:text-black transition-colors">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
                {uploading ? "Envoi…" : "Téléverser"}
                <input type="file" accept="image/*,video/mp4" onChange={upload} className="hidden" data-testid="distinction-upload" />
              </label>
              {form.media_url && (
                <span className="text-xs text-emerald-300 inline-flex items-center gap-1">
                  {form.media_type === "video" ? <Film size={12} /> : <ImageIcon size={12} />} Média joint
                </span>
              )}
            </div>
            {form.media_url && form.media_type !== "video" && (
              <img src={form.media_url} alt="Aperçu" className="mt-3 h-28 w-auto border border-[#27272A] object-cover" data-testid="distinction-preview" />
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Description (optionnel)</label>
            <textarea value={form.description} onChange={set("description")} data-testid="distinction-description" rows={3} placeholder="Ex : 7 buts en 4 matchs, capitaine de l'Équipe 5…" className={`${inputCls} mt-1 resize-none`} />
          </div>
        </div>
        <div className="flex items-center gap-4 mt-8">
          <button onClick={save} disabled={saving} data-testid="distinction-save"
            className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
            <Check size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button onClick={onClose} className="text-xs uppercase tracking-widest text-[#71717A] hover:text-white transition-colors">Annuler</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDistinctions() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/distinctions`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch(() => { toast.error("Erreur de chargement"); setItems([]); });
  }, []);
  useEffect(load, [load]);

  const remove = async (d) => {
    if (!window.confirm(`Retirer « ${d.titre} » (${d.personne}) du Mur des Légendes ?`)) return;
    try {
      await axios.delete(`${API}/admin/distinctions/${d.id}`, { withCredentials: true });
      toast.success("Reconnaissance retirée");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Suppression impossible"));
    }
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#C5A059]" /></div>;

  return (
    <div data-testid="admin-distinctions" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl uppercase font-bold flex items-center gap-2"><Trophy size={18} className="text-[#C5A059]" /> Mur des Légendes</h2>
          <p className="text-sm text-[#71717A] mt-1">
            Les reconnaissances des détections (meilleur joueur, meilleure joueuse, homme du match…) avec photo ou vidéo.
            Visible publiquement sur la page « Mur des Légendes ».
          </p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="distinction-add"
          className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
          <Plus size={13} /> Ajouter
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-center text-[#71717A] text-sm py-12 border border-[#27272A]" data-testid="distinctions-empty">
          Aucune reconnaissance pour le moment. Ajoutez la première (meilleur joueur, meilleure joueuse, homme du match…).
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((d) => (
            <div key={d.id} data-testid={`distinction-card-${d.id.slice(0, 8)}`} className="border border-[#27272A] bg-[#121214] overflow-hidden">
              {d.media_url && d.media_type !== "video" && (
                <img src={d.media_url} alt={d.titre} className="w-full h-44 object-cover" />
              )}
              {d.media_url && d.media_type === "video" && (
                <video src={d.media_url} controls className="w-full h-44 object-cover bg-black" />
              )}
              <div className="p-5">
                <p className="text-[10px] uppercase tracking-widest text-[#C5A059]">{d.programme || "Détection D2R"}{d.date_evenement ? ` · ${d.date_evenement}` : ""}</p>
                <h3 className="font-display text-lg uppercase font-semibold mt-1">{d.titre}</h3>
                <p className="text-sm text-white mt-1 flex items-center gap-2"><User size={13} className="text-[#C5A059]" /> {d.personne}</p>
                {d.description && <p className="text-xs text-[#A1A1AA] mt-2">{d.description}</p>}
                <div className="flex items-center gap-2 mt-4">
                  <button onClick={() => setEditing(d)} data-testid={`distinction-edit-${d.id.slice(0, 8)}`}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors">
                    <Pencil size={11} /> Modifier
                  </button>
                  <button onClick={() => remove(d)} data-testid={`distinction-delete-${d.id.slice(0, 8)}`}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#27272A] text-[#71717A] hover:text-red-400 hover:border-red-400/50 transition-colors">
                    <Trash2 size={11} /> Retirer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <DistinctionForm onSaved={() => { setCreating(false); load(); }} onClose={() => setCreating(false)} />}
      {editing && <DistinctionForm initial={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
    </div>
  );
}
